import * as React from 'react';
import { AddressViewModel } from '@models/common.models';
import { hasAlpha, isValidZip } from '@common/validators';
import { ContractDuplicateAddressReviewModel } from '@models/Contract';
import { Link } from 'react-router-dom';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { SpinnerPage } from '../spinner/spinner-page.component';

export interface AddressReviewComponentProperties {
  verifiedAddress: AddressViewModel;
  addressToVerify: AddressViewModel;
  returnWithAddress: (address: AddressViewModel) => void;
  checkForDuplicateAddresses: (address: AddressViewModel) => Promise<ContractDuplicateAddressReviewModel[]>;
  shouldCheckDuplicates: boolean;
}

export interface AddressReviewComponentState {
  hasDuplicates: boolean;
  checkingForDuplicates: boolean;
  duplicates: ContractDuplicateAddressReviewModel[];
  addressToReturn?: AddressViewModel;
}

export class AddressReviewComponent extends React.Component<
  AddressReviewComponentProperties,
  AddressReviewComponentState
> {
  state: AddressReviewComponentState = {
    hasDuplicates: false,
    duplicates: [],
    checkingForDuplicates: false
  };

  constructor(props: AddressReviewComponentProperties) {
    super(props);
    this.returnVerifiedAddress = this.returnVerifiedAddress.bind(this);
    this.returnOriginalAddress = this.returnOriginalAddress.bind(this);
    this.returnSelectedAddress = this.returnSelectedAddress.bind(this);
    this.checkAddressForDuplicates = this.checkAddressForDuplicates.bind(this);
  }

  checkAddressForDuplicates(address: AddressViewModel) {
    const that = this;
    if (this.props.shouldCheckDuplicates) {
      that.setState({
        checkingForDuplicates: true
      });
      this.props
        .checkForDuplicateAddresses(address)
        .then(contracts => {
          if (contracts.length === 0) {
            that.props.returnWithAddress({ ...address });
          } else {
            contracts.sort(function(a: ContractDuplicateAddressReviewModel, b: ContractDuplicateAddressReviewModel) {
              var n1 = a.addressLine2
                .trim()
                .split(' ')
                .pop();
              var n2 = b.addressLine2
                .trim()
                .split(' ')
                .pop();
              return Number(n1) - Number(n2);
            });
            // nope
            that.setState({
              addressToReturn: address,
              hasDuplicates: true,
              duplicates: contracts,
              checkingForDuplicates: false
            });
          }
        })
        .catch(err => {
          return; // also nope? not really sure here, means we got a 400/500 from the server when checking for duplicates.
        });
    } else {
      that.props.returnWithAddress({ ...address });
    }
  }
  returnSelectedAddress() {
    if (this.state.addressToReturn) {
      this.props.returnWithAddress(this.state.addressToReturn);
    }
  }

  returnVerifiedAddress() {
    this.props.returnWithAddress({ ...this.props.verifiedAddress, isValidated: true });
  }

  returnOriginalAddress() {
    this.props.returnWithAddress(this.props.addressToVerify);
  }

  isValidAddressViewModel(address: AddressViewModel): boolean {
    if (!address) {
      return false;
    }
    if (!address.city || !address.line1 || !address.state || !address.zip) {
      // invalid address, missing required parts
      return false;
    }
    if (!hasAlpha(address.city)) {
      return false;
    }

    if (!address.state || !address.state.abbreviation) {
      return false;
    }

    if (!isValidZip(address.zip)) {
      return false;
    }

    return true;
  }

  renderLineTwo(line2: string) {
    return (
      <span className="white-text">
        {line2}
        <br />
      </span>
    );
  }

  renderDuplicates() {
    if (!this.state.hasDuplicates) {
      return null;
    }
    const contractsView = this.state.duplicates.map((contract: ContractDuplicateAddressReviewModel, index) => {
      return (
        <div className="col-xs-6" key={index}>
          <Link to={getRoute(RouteDefinitions.contract.Details, { id: contract.contractId })} target="_blank">
            <button className="btn btn-primary wider-btn">
              Contract: {contract.contractNumber} <br />
              {contract && contract.addressLine2 ? this.renderLineTwo(contract.addressLine2) : ''}
              {contract.contractStatus!.value}
            </button>
          </Link>
        </div>
      );
    });

    return (
      <div>
        <div className="row">
          <div className="col-xs-12">
            <h1>Duplicate Addresses</h1>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-6">
            <div>
              It looks like there {this.state.duplicates.length === 1 ? 'is' : 'are'} {this.state.duplicates.length}{' '}
              {this.state.duplicates.length === 1 ? 'duplicate' : 'duplicates'} of this address on an existing contract.
            </div>
            <div>
              <button className="btn btn-primary" onClick={this.returnSelectedAddress}>
                Continue creating this Contract
              </button>
            </div>
          </div>
          <div className="col-xs-6">
            <h3>Contracts With duplicate address</h3>
            <hr />
            <div className="row">{contractsView}</div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.checkingForDuplicates) {
      return <SpinnerPage />;
    }
    if (this.state.hasDuplicates) {
      return this.renderDuplicates();
    }

    let verifiedAddress = this.props.verifiedAddress;
    let originalAddress = this.props.addressToVerify;
    if (!this.isValidAddressViewModel(verifiedAddress)) {
      return <div>Whoops! We weren't able to validate the address you entered.</div>;
    }

    if (!originalAddress) {
      originalAddress = {
        id: 0,
        line1: 'Sample Address 1',
        line2: 'Sample Address 2',
        city: 'Sample City',
        state: {
          id: 0,
          name: 'AState',
          abbreviation: 'AA'
        },
        zip: '12345'
      };
    }
    return (
      <div>
        <div>
          <h3>Please select the address you would like to use.</h3>
        </div>
        <hr />
        <div>
          Suggested Address:
          <div>{verifiedAddress.line1}</div>
          <div>{verifiedAddress.line2}</div>
          <div>
            {verifiedAddress.city}, {verifiedAddress.state.abbreviation} {verifiedAddress.zip}
          </div>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              this.checkAddressForDuplicates({ ...verifiedAddress, isValidated: true });
            }}
          >
            Use this Address
          </button>
        </div>
        <hr />
        <div>
          You Entered:
          <div>{originalAddress.line1}</div>
          <div>{originalAddress.line2}</div>
          <div>
            {originalAddress.city}, {originalAddress.state.abbreviation} {originalAddress.zip}
          </div>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              this.checkAddressForDuplicates(originalAddress);
            }}
          >
            Use original Address
          </button>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/components/address-review/address-review.component.tsx